import { ToastContext } from "context/ToastContext";
import { Label } from "layout/typography/Label";
import { Surface } from "pages/HospitalsPage/components/BenefitSelection/Surface";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { useContext, useState } from "react";
import {
  useCreateProcedureCopayListItemMutation,
  useDeleteProcedureCopayListItemMutation,
  useGetProcedureCopayListItemsQuery,
  useUpdateProcedureCopayListItemMutation,
} from "store/queries/procedureCopayList";
import styled from "styled-components";
import {
  IProcedureCopayListItem,
  IProcedureCopayListItemSaveRequest,
} from "types/ProcedureCopayList/ProcedureCopayList";

import { CopayListDialog } from "./CopayListDialog";
import { CopayListTester } from "./CopayListTester";
import { COPAY_LIST_INTRO, COPAY_LIST_RULES } from "./copayListWording";

const StyledDataTable = styled(DataTable)`
  .p-datatable-thead > tr > th {
    background: #ffffff;
  }
  .p-datatable-tbody > tr > td {
    vertical-align: top;
  }
`;

/** A row matches the filter when its code, item or note contains the typed text (any case). */
export const copayListRowMatches = (
  row: IProcedureCopayListItem,
  filter: string
): boolean => {
  const typed = filter.trim().toUpperCase();
  if (!typed) return true;
  return [row.cptCode, row.itemName ?? "add-on", row.note ?? ""].some((text) =>
    text.toUpperCase().includes(typed)
  );
};

const updatedInWords = (row: IProcedureCopayListItem): string =>
  row.updatedAt ? new Date(row.updatedAt).toLocaleDateString("en-US") : "—";

/**
 * CLE-2816 PR 2 — Eligibility › Copay list: which item of UHC's "AMOUNT VARIES BY LOCATION" copay
 * list each CPT / HCPCS code is priced under, for the benefit engine. Global — every hospital uses
 * the same list.
 *
 * At the top, what the list is and the three rules the engine applies (M2 amount for a mapped item,
 * the generic item at its 8H amount for unmapped codes, largest facility charge on multi-procedure
 * estimates). Below, one table with a
 * filter box, an Add / Edit dialog, Remove with a confirm, and "Test codes", a tester. While nothing
 * is saved the table shows the BUILT-IN list, with a banner; the API writes the whole built-in list
 * on the first save, so editing one row never drops the rest. Mirrors the CPT Groups tab.
 */
export function CopayListTab() {
  const toast = useContext(ToastContext);
  const [filter, setFilter] = useState("");
  const { data, isLoading, isError } = useGetProcedureCopayListItemsQuery();
  const [createItem, { isLoading: creating }] =
    useCreateProcedureCopayListItemMutation();
  const [updateItem, { isLoading: updating }] =
    useUpdateProcedureCopayListItemMutation();
  const [deleteItem] = useDeleteProcedureCopayListItemMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [testerOpen, setTesterOpen] = useState(false);
  const [editing, setEditing] = useState<IProcedureCopayListItem | null>(null);

  const rows = data?.rows ?? [];
  const shown = rows.filter((row) => copayListRowMatches(row, filter));

  const fail = (summary: string, error: any, fallback: string) =>
    toast?.current?.show({
      severity: "error",
      summary,
      detail: error?.data?.message || error?.message || fallback,
    });

  const handleSave = async (body: IProcedureCopayListItemSaveRequest) => {
    try {
      if (editing) {
        await updateItem({ cptCode: editing.cptCode, body }).unwrap();
      } else {
        await createItem(body).unwrap();
      }
      setDialogOpen(false);
      toast?.current?.show({
        severity: "success",
        summary: "Code saved",
        detail: "The next eligibility check uses it.",
      });
    } catch (error: any) {
      fail("Not saved", error, "The code could not be saved.");
    }
  };

  const remove = async (row: IProcedureCopayListItem) => {
    // eslint-disable-next-line no-alert
    const sure = window.confirm(
      `Remove ${row.cptCode} from the copay list? The next eligibility check treats it as not on the list (the generic OUTPATIENT HOSPITAL CARE item, unless another code on the estimate is mapped).`
    );
    if (!sure) return;
    try {
      await deleteItem(row.cptCode).unwrap();
    } catch (error: any) {
      fail("Not removed", error, "The code could not be removed.");
    }
  };

  const openEditor = (row: IProcedureCopayListItem | null) => {
    setEditing(row);
    setDialogOpen(true);
  };

  const codeBody = (row: IProcedureCopayListItem) => (
    <span
      className="font-mono"
      data-testid={`copayList.row.${row.cptCode}.code`}
    >
      {row.cptCode}
    </span>
  );

  const itemBody = (row: IProcedureCopayListItem) => (
    <span data-testid={`copayList.row.${row.cptCode}.item`}>
      {row.itemName ?? (
        <span className="text-color-secondary">
          Add-on — never decides the item
        </span>
      )}
    </span>
  );

  const noteBody = (row: IProcedureCopayListItem) => (
    <span className="text-sm">
      {row.note || <span className="text-color-secondary">—</span>}
    </span>
  );

  const updatedBody = (row: IProcedureCopayListItem) => (
    <span className="text-sm">{updatedInWords(row)}</span>
  );

  const actionsBody = (row: IProcedureCopayListItem) => (
    <div className="flex gap-1 justify-content-end">
      <Button
        type="button"
        icon="pi pi-pencil"
        className="p-button-text p-button-sm"
        aria-label={`Edit ${row.cptCode}`}
        data-testid={`copayList.row.${row.cptCode}.edit`}
        onClick={() => openEditor(row)}
      />
      <Button
        type="button"
        icon="pi pi-trash"
        className="p-button-text p-button-sm p-button-danger"
        aria-label={`Remove ${row.cptCode}`}
        data-testid={`copayList.row.${row.cptCode}.remove`}
        onClick={() => remove(row)}
      />
    </div>
  );

  return (
    <div className="flex flex-column gap-3" data-testid="copayListTab">
      <Surface>
        <p className="mt-0 mb-2" data-testid="copayList.intro">
          {COPAY_LIST_INTRO}
        </p>
        <ul className="m-0 pl-4" data-testid="copayList.rules">
          {COPAY_LIST_RULES.map((rule) => (
            <li key={rule}>{rule}</li>
          ))}
        </ul>
      </Surface>

      <Surface>
        <div className="flex flex-wrap gap-2 mb-2 align-items-end">
          <Button
            type="button"
            icon="pi pi-plus"
            label="Add code"
            className="p-button-sm"
            data-testid="copayList.add"
            onClick={() => openEditor(null)}
          />
          <Button
            type="button"
            icon="pi pi-play"
            label="Test codes"
            className="p-button-sm p-button-outlined"
            data-testid="copayList.test"
            onClick={() => setTesterOpen(true)}
          />
          <div className="flex flex-column gap-1 ml-auto">
            <Label htmlFor="copayList.filter">Filter</Label>
            <InputText
              id="copayList.filter"
              data-testid="copayList.filter"
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              placeholder="Code, item or note"
              style={{ minWidth: "14rem" }}
            />
          </div>
        </div>

        {isLoading && (
          <div className="flex justify-content-center p-3">
            <ProgressSpinner
              style={{ width: "2rem", height: "2rem" }}
              data-testid="copayList.loading"
            />
          </div>
        )}
        {isError && (
          <Message
            severity="error"
            text="The copay list could not be loaded."
            data-testid="copayList.error"
          />
        )}
        {!isLoading && !isError && data?.builtIn && (
          <Message
            severity="info"
            className="mb-2 w-full justify-content-start"
            text="Showing the built-in list: nothing is saved yet, and the engine reads these codes. The first save writes the whole list, then your change."
            data-testid="copayList.builtIn"
          />
        )}
        {!isLoading && !isError && data && (
          <StyledDataTable
            value={shown}
            dataKey="cptCode"
            className="w-full"
            data-testid="copayList.table"
            emptyMessage="No code matches the filter."
          >
            <Column header="Code" body={codeBody} style={{ width: "7rem" }} />
            <Column header="Item" body={itemBody} />
            <Column header="Note" body={noteBody} />
            <Column
              header="Updated"
              body={updatedBody}
              style={{ width: "8rem" }}
            />
            <Column body={actionsBody} style={{ width: "6rem" }} />
          </StyledDataTable>
        )}
      </Surface>

      <CopayListTester
        visible={testerOpen}
        onHide={() => setTesterOpen(false)}
      />

      <CopayListDialog
        visible={dialogOpen}
        row={editing}
        rows={rows}
        itemNames={data?.itemNames ?? []}
        saving={creating || updating}
        onSave={handleSave}
        onHide={() => setDialogOpen(false)}
      />
    </div>
  );
}
